import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function AsphaltShingleRoofPage() {
  useEffect(() => {
    document.title = 'Asphalt Shingle Roofing | Architectural Shingles | 3-Tab Shingles | NWA Installation';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium asphalt shingle roofing in Northwest Arkansas. Expert installation of architectural asphalt roof shingles, 3 tab asphalt roof shingles, and asphalt laminated shingles. Highest rated asphalt shingles with lifetime warranties. Serving Fayetteville, Rogers, Bentonville.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'asphalt shingle roof, architectural asphalt roof shingles, 3 tab asphalt roof shingles, asphalt laminated shingles, architectural shingles, highest rated asphalt shingles, asphalt shingles 3 tab');
    }
  }, []);

  const shingleTypes = [
    {
      name: 'Architectural Asphalt Roof Shingles',
      description: 'Premium dimensional shingles with enhanced durability and aesthetic appeal',
      features: [
        '30-50 year lifespan',
        'Multi-dimensional appearance',
        'Superior wind resistance (130+ mph)',
        'Enhanced hail impact resistance',
        'Lifetime warranty options available',
        'Increased home value'
      ],
      image: 'https://public.readdy.ai/ai/img_res/041970dd86af97a040471c6965f0bc81.jpg'
    },
    {
      name: '3 Tab Asphalt Roof Shingles',
      description: 'Cost-effective traditional shingles with reliable performance',
      features: [
        '20-25 year lifespan',
        'Uniform flat appearance',
        'Budget-friendly option',
        'Proven reliability',
        'Easy replacement and repair',
        'Wide color selection'
      ],
      image: 'https://readdy.ai/api/search-image?query=traditional%203-tab%20asphalt%20shingles%20on%20residential%20home%20showing%20classic%20uniform%20pattern%20and%20clean%20lines%2C%20cost-effective%20roofing%20solution%20with%20professional%20installation%2C%20simple%20elegant%20design&width=600&height=400&seq=3tab-shingles&orientation=landscape'
    },
    {
      name: 'Asphalt Laminated Shingles',
      description: 'Multi-layered shingles combining durability with dimensional beauty',
      features: [
        '30-40 year lifespan',
        'Laminated construction',
        'Enhanced thickness and durability',
        'Excellent weather resistance',
        'Premium appearance',
        'Superior warranty coverage'
      ],
      image: 'https://readdy.ai/api/search-image?query=laminated%20asphalt%20shingles%20showing%20multi-layer%20construction%20and%20premium%20quality%2C%20dimensional%20roofing%20materials%20with%20enhanced%20durability%2C%20professional%20residential%20installation%20with%20rich%20texture&width=600&height=400&seq=laminated-shingles&orientation=landscape'
    }
  ];

  const faqs = [
    {
      question: 'What is the difference between architectural shingles and 3-tab shingles?',
      answer: 'Architectural asphalt roof shingles are premium dimensional shingles with multiple layers creating a textured, high-end appearance. They are thicker, more durable, and typically last 30-50 years with superior wind and hail resistance. 3 tab asphalt roof shingles are traditional flat shingles with a uniform appearance, more budget-friendly, and typically last 20-25 years. Architectural shingles add more curb appeal and home value, while asphalt shingles 3 tab offer reliable protection at a lower cost.'
    },
    {
      question: 'Are architectural shingles worth the extra cost?',
      answer: 'Yes, architectural asphalt roof shingles are worth the investment for most homeowners. They last 50-100% longer than 3 tab asphalt roof shingles, offer superior wind and hail resistance, significantly enhance curb appeal and home value, and come with better warranty coverage. The higher upfront cost is offset by longer lifespan and fewer repairs. Many insurance companies also offer discounts for impact-resistant architectural shingles.'
    },
    {
      question: 'What are the highest rated asphalt shingles?',
      answer: 'The highest rated asphalt shingles include premium architectural lines from leading manufacturers like GAF Timberline HDZ, Owens Corning Duration, CertainTeed Landmark, and Tamko Heritage. These architectural asphalt roof shingles feature advanced technology including algae resistance, impact resistance, and enhanced wind ratings. Contractors in our network install only the highest rated asphalt shingles backed by comprehensive manufacturer warranties and their own workmanship guarantees.'
    },
    {
      question: 'How long do asphalt laminated shingles last?',
      answer: 'Asphalt laminated shingles typically last 30-40 years with proper installation and maintenance. These multi-layered shingles offer enhanced durability compared to standard 3 tab asphalt roof shingles. Lifespan depends on factors including climate, ventilation, installation quality, and maintenance. Northwest Arkansas weather can be harsh, but premium asphalt laminated shingles with proper installation provide decades of reliable protection.'
    },
    {
      question: 'Can I install architectural shingles over existing 3-tab shingles?',
      answer: 'While building codes in some areas allow one layer of shingles over existing roofing, contractors typically recommend complete removal for architectural asphalt roof shingles installation. Removing old 3 tab asphalt roof shingles allows inspection of roof decking, ensures proper installation, maximizes new shingle lifespan, and maintains manufacturer warranty coverage. The small additional cost of removal is worth the long-term benefits.'
    },
    {
      question: 'What colors are available for asphalt shingle roofs?',
      answer: 'Asphalt shingle roofs are available in dozens of colors from neutral grays and browns to bold blues and greens. Architectural asphalt roof shingles offer the widest color selection with multi-tonal blends that create dimensional appearance. Popular choices in Northwest Arkansas include weathered wood tones, charcoal and slate grays, and earth-tone browns that complement regional architecture. Contractors provide samples to help you choose the perfect color for your home.'
    }
  ];

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-orange-600 to-orange-800">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20residential%20home%20with%20premium%20architectural%20asphalt%20shingle%20roof%20installation%2C%20dimensional%20shingles%20showing%20rich%20texture%20and%20color%2C%20professional%20roofing%20craftsmanship%2C%20Northwest%20Arkansas%20suburban%20neighborhood%2C%20modern%20home%20exterior%20with%20quality%20materials&width=1920&height=800&seq=asphalt-shingle-hero-nwa&orientation=landscape"
            alt="Asphalt shingle roofing Northwest Arkansas"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Premium Asphalt Shingle Roofing in NWA
              </h1>
              <p className="text-xl mb-8 leading-relaxed">
                Connect with expert installers of <strong>architectural asphalt roof shingles</strong>, <strong>3 tab asphalt roof shingles</strong>, and <strong>asphalt laminated shingles</strong> throughout Northwest Arkansas. Get the <strong>highest rated asphalt shingles</strong> with lifetime warranties for Fayetteville, Springdale, Rogers, and Bentonville homes. Premium <strong>architectural shingles</strong> that enhance curb appeal and protect your investment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:4795551234"
                  className="bg-white text-orange-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 transition-all shadow-xl text-center whitespace-nowrap"
                >
                  Call (479) 555-1234
                </a>
                <a
                  href="#quote-form"
                  className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-slate-800 hover:to-black transition-all shadow-xl text-center whitespace-nowrap"
                >
                  Get Free Estimate
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <QuoteForm variant="compact" />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Expert Asphalt Shingle Roof Installation in Northwest Arkansas
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When choosing roofing materials for your Northwest Arkansas home, <strong>asphalt shingle roof</strong> systems offer the perfect combination of durability, affordability, and aesthetic appeal. We connect homeowners throughout Fayetteville, Springdale, Rogers, Bentonville, and surrounding communities with experienced contractors who specialize in installing premium <strong>architectural asphalt roof shingles</strong>, reliable <strong>3 tab asphalt roof shingles</strong>, and durable <strong>asphalt laminated shingles</strong>. These professionals use only the <strong>highest rated asphalt shingles</strong> from leading manufacturers, backed by comprehensive warranties and expert installation.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Asphalt shingle roof</strong> systems dominate the residential roofing market for good reason. They provide excellent weather protection against Northwest Arkansas's harsh climate including severe hail storms, high winds, heavy rain, and extreme temperature fluctuations. Modern <strong>architectural shingles</strong> feature advanced technology including impact resistance, algae resistance, and enhanced wind ratings up to 130 mph. Whether you choose premium <strong>architectural asphalt roof shingles</strong> for maximum durability and curb appeal or cost-effective <strong>asphalt shingles 3 tab</strong> for reliable protection, you'll get decades of performance.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                The contractors in our network are certified installers of the <strong>highest rated asphalt shingles</strong> from manufacturers like GAF, Owens Corning, CertainTeed, and Tamko. They stay current with the latest installation techniques and manufacturer requirements to ensure your warranty remains valid. Their expertise with <strong>asphalt laminated shingles</strong> and premium <strong>architectural shingles</strong> ensures proper installation that maximizes lifespan and performance while enhancing your home's beauty and value.
              </p>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">Types of Asphalt Shingles We Install</h3>
              
              <div className="space-y-8 mb-8">
                {shingleTypes.map((type, index) => (
                  <div key={index} className="bg-orange-50 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="h-64 md:h-auto">
                        <img
                          src={type.image}
                          alt={type.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-6">
                        <h4 className="text-2xl font-bold text-gray-900 mb-3">{type.name}</h4>
                        <p className="text-gray-700 leading-relaxed mb-4">{type.description}</p>
                        <ul className="space-y-2">
                          {type.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-checkbox-circle-line text-green-500 mr-2 mt-1"></i>
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">Comparing Asphalt Shingle Options</h3>
              
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg">
                  <thead className="bg-orange-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-bold">Feature</th>
                      <th className="px-6 py-4 text-left font-bold">3-Tab Shingles</th>
                      <th className="px-6 py-4 text-left font-bold">Laminated Shingles</th>
                      <th className="px-6 py-4 text-left font-bold">Architectural Shingles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 font-semibold">Lifespan</td>
                      <td className="px-6 py-4">20-25 years</td>
                      <td className="px-6 py-4">30-40 years</td>
                      <td className="px-6 py-4">30-50 years</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold">Wind Rating</td>
                      <td className="px-6 py-4">60-70 mph</td>
                      <td className="px-6 py-4">110-120 mph</td>
                      <td className="px-6 py-4">110-130 mph</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold">Appearance</td>
                      <td className="px-6 py-4">Flat, uniform</td>
                      <td className="px-6 py-4">Dimensional</td>
                      <td className="px-6 py-4">Premium dimensional</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold">Warranty</td>
                      <td className="px-6 py-4">Limited</td>
                      <td className="px-6 py-4">Enhanced</td>
                      <td className="px-6 py-4">Lifetime options</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold">Cost</td>
                      <td className="px-6 py-4">Budget-friendly</td>
                      <td className="px-6 py-4">Mid-range</td>
                      <td className="px-6 py-4">Premium</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold">Home Value</td>
                      <td className="px-6 py-4">Standard</td>
                      <td className="px-6 py-4">Enhanced</td>
                      <td className="px-6 py-4">Maximum</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                This comparison shows why <strong>architectural asphalt roof shingles</strong> are the most popular choice for Northwest Arkansas homeowners. While <strong>3 tab asphalt roof shingles</strong> offer reliable protection at a lower cost, the enhanced durability, superior weather resistance, and increased home value of <strong>architectural shingles</strong> make them worth the investment. <strong>Asphalt laminated shingles</strong> provide an excellent middle ground with enhanced performance at a moderate price point.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-16 bg-gradient-to-br from-orange-600 to-orange-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Get Free Quotes for Your Roofing Project
              </h2>
              <p className="text-xl text-orange-100">
                Connect with qualified contractors specializing in asphalt shingles, metal, and tile roofing
              </p>
            </div>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Related Roofing Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link to="/services/shingle-roofs" className="group bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-home-gear-line text-4xl text-orange-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Roof Replacement
              </h3>
              <p className="text-gray-600 mb-4">
                Complete roof replacement services with premium materials and expert installation.
              </p>
              <span className="text-orange-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/services/roof-inspection" className="group bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-search-eye-line text-4xl text-orange-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Free Roof Inspection
              </h3>
              <p className="text-gray-600 mb-4">
                Professional roof inspections to assess condition and identify any issues.
              </p>
              <span className="text-orange-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/services/storm-damage" className="group bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-thunderstorms-line text-4xl text-orange-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Storm Damage Repair
              </h3>
              <p className="text-gray-600 mb-4">
                Expert storm damage assessment and repair for hail and wind damage.
              </p>
              <span className="text-orange-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
